/**
 * Darstellung (29.09.2026): Automatisch (folgt dem Gerät, Standard seit 0397, Inhaber 21.10.2026), Hell oder Dunkel.
 * Am <html> stehen data-theme-wahl (die Wahl) und data-theme (das wirksame Schema light | dark; danach richten sich die
 * Tokens in portal.css und buehne.css). Die Wahl liegt je Person in user_preferences.theme mit theme_chosen_at (0120, 0121)
 * und zusätzlich im Browser (localStorage „tk-theme“), damit sie schon vor der Anmeldung und ohne Flackern greift:
 * public/boot.js setzt beides vor dem ersten Zeichnen und folgt bei „Automatisch“ dem Wechsel des Geräts. Ohne Wahl gilt
 * überall Automatisch (Anmeldung, Registrierung, Einladung, Demo, Portal); Rechnungs- und Vertragspapier bleibt weiß.
 * Eine Vorgabe in der Adresse (?theme=, z. B. für Bildschirmfotos und Einbettungen) gilt für diese Seite und wird weder
 * gemerkt noch vom Konto überschrieben.
 */
export type ThemePreference = "light" | "dark" | "system";
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "tk-theme";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system";
export const THEME_PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

/** <html> der Seite; beim Zeichnen ohne Browser (Tests, Server) null. */
function root(): HTMLElement | null {
  return typeof document === "undefined" ? null : document.documentElement;
}

/** Gültige Wahl oder null (alte oder fremde Werte, leere Zeilen). */
export function asThemePreference(value: unknown): ThemePreference | null {
  return value === "light" || value === "dark" || value === "system" ? value : null;
}

/** Wirksames Schema einer Wahl: „Automatisch“ folgt prefers-color-scheme. */
export function resolveTheme(preference: ThemePreference, systemDark: boolean): Theme {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

export function systemPrefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Im Browser gemerkte Wahl; ohne Wahl (oder ohne Speicher) der Standard „Automatisch“. */
export function storedThemePreference(): ThemePreference {
  try {
    return asThemePreference(localStorage.getItem(THEME_STORAGE_KEY)) ?? DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

/** Aktuelle Wahl dieser Seite (am <html>, gesetzt von boot.js bzw. setThemePreference). */
export function currentThemePreference(): ThemePreference {
  return asThemePreference(root()?.dataset.themeWahl) ?? storedThemePreference();
}

/** Wirksames Schema dieser Seite. */
export function currentTheme(): Theme {
  const t = root()?.dataset.theme;
  return t === "dark" || t === "light" ? t : resolveTheme(currentThemePreference(), systemPrefersDark());
}

/** true, wenn die Adresse das Schema vorgibt (?theme=): dann gilt sie für diese Seite, das Konto überschreibt sie nicht. */
export function themeFromAddress(): boolean {
  return root()?.dataset.themeUrl === "1";
}

/**
 * Setzt die Wahl sofort (data-theme-wahl, data-theme) und merkt sie im Browser. Den Wechsel des Geräts bei „Automatisch“
 * verfolgt boot.js (liest data-theme-wahl). Gibt das wirksame Schema zurück.
 */
export function setThemePreference(preference: ThemePreference): Theme {
  const html = root();
  const theme = resolveTheme(preference, systemPrefersDark());
  if (html) {
    html.dataset.themeWahl = preference;
    html.dataset.theme = theme;
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* ohne Speicher gilt die Wahl bis zum Neuladen */
  }
  return theme;
}

/** Schalter „Hell / Dunkel“ (öffentliche Seiten, Betreiber-Konsole, Kontomenü): feste Wahl, Gegenteil des wirksamen Schemas. */
export function toggleTheme(): ThemePreference {
  const next: ThemePreference = currentTheme() === "dark" ? "light" : "dark";
  setThemePreference(next);
  return next;
}

export function isEmbed(): boolean {
  return root()?.dataset.embed === "true";
}
